/**
 * Kota Nova client boot (P0-T03..T09).
 * Flow: tier detection -> engine -> scene (ground, sky, 20 test boxes) ->
 * physics worker + player + camera -> network -> HUD. `window.__KN` is installed
 * in every non-production build (SPEC-13 §2).
 */
import { detectTier, type TierInfo } from './engine/tiers';
import { createEngine } from './engine/create';
import { createPipeline, type PipelineHandle } from './engine/pipeline';
import { createShadows, type ShadowHandle } from './engine/shadows';
import { applyShadowRoles, BlobShadows } from './world/shadowRoles';
import { SkySystem } from './world/sky';
import { StreamingClient } from './world/streamingClient';
import { PropField } from './world/lod';
import { WaterField } from './world/water';
import { FountainFx } from './world/fountain';
import { WeatherFx, type RainTierCounts } from './world/weather';
import { CrowdField } from './crowd/crowd';
import { CrowdDriver } from './crowd/driver';
import { Traffic } from './transit/traffic';
import { CityEnv } from './world/materials/cityEnv';
import { createRegistry, prepareCellMaterials } from './world/materials/cityMaterials';
import { QualityController, SHED_STEP_NAMES } from './engine/quality';
import { installKN, FrameStats, type KNGlobal } from './debug/kn';
import { maybeStartPerfRunner } from './debug/perf-runner';
import { createPlayer, RemoteAvatarPool, TEST_OBSTACLES } from './avatar/player';
import { createCamera, createInputCollector } from './avatar/camera';
import { connectNet, attachNetPump, type NetHandle } from './net/net';
import { initUi } from './ui/panels';
import { t } from './ui/i18n';
import type { ClockSync, Tier } from '@kota-nova/shared';
import { worldMinutes } from '@kota-nova/sim';
import { initErrorTracking } from './ui/error-tracking';

const PRODUCTION = import.meta.env.PROD;

async function boot(): Promise<void> {
  await initErrorTracking();
  const canvas = document.getElementById('render-canvas') as HTMLCanvasElement;
  let tierInfoRef: TierInfo | null = null;
  let renderScaleNow = 1;
  let pinnedHour: number | null = null; // Sky panel clock pin (null = follow server)
  const ui = initUi({
    onTierChange: () => {
      location.reload(); // tier applies on reload (SPEC-11 §3 Setup)
    },
    onOverlayChange: () => {
      location.reload(); // __KN arms at boot; reload so the HUD has data (and tap-to-shed works)
    },
    onSoundChange: () => void 0,
    onLanguageChange: () => void 0,
    deviceInfo: () => (tierInfoRef ? deviceInfoText(tierInfoRef, renderScaleNow) : 'detecting…'),
    statsLine: () => statsLine(),
    onIntroEnter: () => void 0,
    onForceShed: () => knRef?.forceShed(),
    onSetTimeHours: (hour) => {
      pinnedHour = hour;
      sky.clock = { worldTime: hour * 3600 * 1000, timeScale: 0, serverNow: Date.now() };
    },
    onFollowClock: () => {
      pinnedHour = null;
      sky.clock = clock;
    },
    onSetWeather: (kind) => {
      sky.weatherOverride = kind as never;
    },
    skyState: () => {
      const hour = pinnedHour ?? (((Math.floor(worldMinutes(clock, Date.now())) % 1440) + 1440) % 1440) / 60;
      return { hour, weather: sky.weatherKind, pinned: pinnedHour !== null };
    },
  });

  ui.setLoading('loading.tier', 0.05);
  const tierOverride =
    /#(low|mid|high)/.test(location.hash) || /[?&]tier=(low|mid|high)\b/.test(location.search);
  const tierInfo: TierInfo = await detectTier({
    skipBenchmark: tierOverride || new URLSearchParams(location.search).has('nobench'),
  });
  const tier: Tier = tierInfo.tier;
  tierInfoRef = tierInfo;
  renderScaleNow = tierInfo.renderScale;
  ui.refreshDeviceInfo();

  ui.setLoading('loading.engine', 0.25);
  const { engine, babylon } = await createEngine(canvas, tier);
  const scene = new babylon.Scene(engine);
  scene.skipPointerMovePicking = true;

  ui.setLoading('loading.world', 0.5);
  // world streaming (P1-T05): ?world=0 keeps the P0 test scene (used by P0
  // e2e/perf paths); otherwise the streamed slice replaces it entirely.
  const params = new URLSearchParams(location.search);
  const worldOn = params.get('world') !== '0';
  const assetBase = params.get('assets') ?? 'https://assets.sintim.io';
  if (!worldOn) {
    // ground plane
    const ground = babylon.MeshBuilder.CreateGround(
      'ground',
      { width: 512, height: 512, subdivisions: 1 },
      scene,
    );
    const groundMat = new babylon.StandardMaterial('groundMat', scene);
    groundMat.diffuseColor = new babylon.Color3(0.32, 0.34, 0.36);
    groundMat.specularColor = new babylon.Color3(0.05, 0.05, 0.06);
    ground.material = groundMat;
    ground.freezeWorldMatrix();

    // 20 test boxes (P0-T07)
    const boxMat = new babylon.StandardMaterial('boxMat', scene);
    boxMat.diffuseColor = new babylon.Color3(0.55, 0.58, 0.62);
    for (let i = 0; i < 20; i++) {
      const b = babylon.MeshBuilder.CreateBox(`box${i}`, { size: 2 + (i % 4) }, scene);
      const a = (i / 20) * Math.PI * 2;
      b.position.set(Math.cos(a) * (18 + (i % 6) * 4), 1 + (i % 4) / 2, Math.sin(a) * (18 + (i % 6) * 4));
      b.material = boxMat;
      b.freezeWorldMatrix();
    }
    // test course obstacles (P0-T08) rendered so the steps are visible
    for (let i = 0; i < TEST_OBSTACLES.length; i++) {
      const [x, y, z, hx, hy, hz] = TEST_OBSTACLES[i]!;
      const m = babylon.MeshBuilder.CreateBox(
        `step${i}`,
        { width: hx * 2, height: hy * 2, depth: hz * 2 },
        scene,
      );
      m.position.set(x, y, z);
      m.material = boxMat;
      m.freezeWorldMatrix();
    }
  }

  const sky = new SkySystem(babylon as unknown as never, scene, {
    cloudOctaves: tier === 'low' ? 3 : tier === 'mid' ? 4 : 5,
    worldSeed: 1337,
    weatherOverride: null,
    envSize: tier === 'low' ? 32 : tier === 'mid' ? 64 : 128,
  });

  const camera = createCamera(babylon, scene);
  const input = createInputCollector();

  let streaming: StreamingClient | null = null;
  const cityMats = createRegistry();
  const props = new PropField({
    scene,
    tier,
    onContainer: (c) => prepareCellMaterials(scene, c, cityMats),
  });
  const water = new WaterField(babylon, scene, tier);
  const fountains = new FountainFx(scene);
  // P1-T12: rain/snow particles (SPEC-10 §3 rates per tier); ?rain=0 disables
  const weatherFx = new WeatherFx(
    scene,
    (tier === 'low' ? 1400 : tier === 'mid' ? 3200 : 6500) as RainTierCounts,
    !params.has('rain') || params.get('rain') !== '0',
  );

  // P1-T11: placeholder life (feature flags ?crowd=0 / ?traffic=0)
  const crowdOn = params.get('crowd') !== '0';
  const trafficOn = params.get('traffic') !== '0';
  const CROWD_PARAMS = {
    low: { visible: 150, population: 2600, radius: 110, capMid: 90, capFar: 150 },
    mid: { visible: 320, population: 5000, radius: 160, capMid: 200, capFar: 300 },
    high: { visible: 500, population: 8000, radius: 240, capMid: 320, capFar: 480 },
  } as const;
  const TRAFFIC_PARAMS = {
    low: { simCars: 320, drawCap: 60, radius: 220, shadows: false },
    mid: { simCars: 600, drawCap: 200, radius: 340, shadows: true },
    high: { simCars: 900, drawCap: 300, radius: 450, shadows: true },
  } as const;
  const crowdField = crowdOn
    ? new CrowdField(engine, scene, {
        tier,
        capMid: CROWD_PARAMS[tier].capMid,
        capFar: CROWD_PARAMS[tier].capFar,
        radius: CROWD_PARAMS[tier].radius,
      })
    : null;
  const crowd =
    crowdField &&
    new CrowdDriver(crowdField, {
      tier,
      visible: CROWD_PARAMS[tier].visible,
      midRadius: tier === 'low' ? 34 : tier === 'mid' ? 60 : 85,
      radius: CROWD_PARAMS[tier].radius,
      population: CROWD_PARAMS[tier].population,
      enabled: true,
    });
  const traffic = trafficOn ? new Traffic(engine, scene, { ...TRAFFIC_PARAMS[tier] }) : null;
  let envTimeS = 8 * 3600; // shader clock (seconds) for flicker/sway animation

  const pipeline: PipelineHandle | null = createPipeline(babylon, scene, tier);
  const shadows: ShadowHandle | null = createShadows(babylon, scene, sky.sun, tier);
  // P1-T11: near crowd (High only) and cars cast (SPEC-01 §6)
  if (crowdField && tier === 'high') for (const m of crowdField.casters()) shadows?.addCaster(m);
  if (traffic && tier !== 'low') for (const m of traffic.casters()) shadows?.addCaster(m);
  // P1-T10: Low tier has no shadow map — people get radial blob shadows
  const blobs: BlobShadows | null = shadows ? null : new BlobShadows(babylon, scene);
  let remotesDirty = true;
  const castRemotes = new Set<string>();

  // network (optional in P0: the world works offline until a token is present)
  let net: NetHandle | null = null;
  let clock: ClockSync = { worldTime: 12 * 60 * 60000, timeScale: 30, serverNow: Date.now() };
  // Sky panel pin (owner review): while set, the server clock keeps syncing
  // for network but the sky holds this hour (timeScale 0 = frozen sun).
  sky.clock = clock;
  const remotes = new RemoteAvatarPool(babylon, scene);

  const player = createPlayer(
    { babylon, scene },
    {
      send: (type, payload) => net?.send(type, payload),
      getClock: () => clock,
    },
  );

  if (worldOn) {
    // (0,0) is mid-block in the slice layout; put the player on the -64 avenue
    player.teleport(-64, 2, 0);
    streaming = new StreamingClient({
      scene,
      babylon,
      tier,
      assetBase,
      // slice (-2..1) + 4 placeholder rings (-6..5) for outer-ring cost (T05b)
      sliceBounds: { min: -6, max: 5 },
      cameraPos: () => ({
        x: player.state.x,
        z: player.state.z,
        altitude: player.state.y,
        fx: 0,
        fz: 1,
      }),
      onCellLoaded: (container) => {
        // P1-T06: dedupe per-cell materials + attach city plugins
        prepareCellMaterials(scene, container, cityMats);
        // P1-T10: solid meshes cast, everything solid receives (SPEC-01 §6)
        applyShadowRoles(shadows, container.meshes, true);
      },
      onInstances: (cell, list) => {
        props.addCell(cell, list);
        fountains.setCell(cell, list.instances);
      },
      onColliders: (cell, boxes) => {
        const out: Array<[number, number, number, number, number, number]> = [];
        for (const b of boxes) {
          const cx = (b.x0 + b.x1) / 2;
          const cy = (b.y0 + b.y1) / 2;
          const cz = (b.z0 + b.z1) / 2;
          out.push([cx, cy, cz, (b.x1 - b.x0) / 2, (b.y1 - b.y0) / 2, (b.z1 - b.z0) / 2]);
        }
        player.sendColliders('add', cell, out);
      },
      onCellUnloaded: (cell) => {
        props.removeCell(cell);
        fountains.removeCell(cell);
        player.sendColliders('remove', cell, []);
      },
    });
    void streaming
      .configureLoader(engine)
      .then(() => streaming?.fetchManifest())
      .then((m) => {
        if (m)
          console.log(`[stream] manifest: ${String(Object.keys(m.cells).length)} cells from ${assetBase}`);
        if (m && Object.keys(m.shared ?? {}).length > 0) {
          // P1-T07: shared props load once; instance lists stream per cell
          void props
            .loadShared(m.shared, assetBase)
            .then((n) => {
              console.log(`[props] ${String(n)} shared meshes`);
              // P1-T10: near trees cast (thin-instance frustum skipped:
              // alwaysSelectAsActiveMesh) — SPEC-01 §6 allows near trees
              for (const mesh of props.casterMeshes()) shadows?.addCaster(mesh);
            })
            .catch((err: unknown) => console.warn('[props] failed:', (err as Error).message));
        }
      })
      .catch((err: unknown) => console.warn('[stream] disabled:', (err as Error).message));
  }

  // join when explicitly requested (?auto=0 disables) — the dev server can mint a token
  if (new URLSearchParams(location.search).get('auto') !== '0') {
    ui.setLoading('loading.net', 0.8);
    try {
      net = await connectNet({
        onClock: (sync) => {
          clock = sync;
          if (pinnedHour === null) sky.clock = sync;
        },
        onPlayerLeft: (id) => remotes.remove(id),
        onCorrect: (c) => player.sendCorrect(c),
        onStatus: (connected) => ui.toast(connected ? t('net.connected') : t('net.disconnected')),
      });
      attachNetPump(
        net,
        () => net?.userId ?? '',
        (id, x, y, z, yaw, anim, look) => {
          let color = '#5fd3c6';
          try {
            color = (JSON.parse(look) as { color?: string }).color ?? color;
          } catch {
            /* keep default */
          }
          remotes.push(id, x, y, z, yaw, anim, performance.now(), color);
          remotesDirty = true;
        },
      );
    } catch (err) {
      console.warn('[net] offline mode:', (err as Error).message);
      ui.toast('Offline: ' + (err as Error).message);
    }
  }

  // __KN hooks + quality controller (P0-T05/T06)
  const shedLog: string[] = [];
  // __KN is on for dev builds; production builds enable it only when the URL
  // asks (?perf= or ?kn=1) so the on-device runner works on staging (SPEC-13 §2.1)
  const knEnabled =
    !PRODUCTION ||
    new URLSearchParams(location.search).has('perf') ||
    new URLSearchParams(location.search).has('kn') ||
    // the perf overlay is a debug surface: enabling it in Setup also arms
    // __KN so tap-to-shed works on phones (?kn=1 needs a console otherwise)
    (() => {
      try {
        return localStorage.getItem('kn_overlay') === '1';
      } catch {
        return false; // private mode
      }
    })();
  const kn: KNGlobal | null = knEnabled
    ? installKN(babylon, engine, scene, { tier, gpu: tierInfo.gpu, shed: shedLog })
    : null;
  knRef = kn;

  // SPEC-02 A-03 hook: __KN.streaming.lruMB = 0 disables the cell cache;
  // ?lru=<MB> pins it at boot so device captures (P1-G04) are one link, no console
  if (kn && streaming) {
    (kn as unknown as { streaming: StreamingClient }).streaming = streaming;
    const lruParam = Number(params.get('lru') ?? '');
    if (params.has('lru') && Number.isFinite(lruParam) && lruParam >= 0) streaming.lruMB = lruParam;
  }
  if (kn) (kn as unknown as { props?: PropField }).props = props;
  // P1-T11 test hooks: crowd + traffic
  if (kn)
    (kn as unknown as { crowdStats?: () => { visible: number; sim: number; routes: number } }).crowdStats =
      () => crowd?.stats() ?? { visible: 0, sim: 0, routes: 0 };
  if (kn)
    (kn as unknown as { trafficStats?: () => { sim: number; drawn: number } }).trafficStats = () =>
      traffic?.stats() ?? { sim: 0, drawn: 0 };
  // P1-T10 test hook: R-03 shadow culling counts
  if (kn)
    (
      kn as unknown as {
        shadowStats?: () => { total: number; perCascade: number[]; culled: boolean[]; err: string };
      }
    ).shadowStats = () => shadows?.stats() ?? { total: 0, perCascade: [], culled: [], err: 'no shadows' };
  // dev/test harness access (Playwright scripts hook Effect/Logger here)
  if (kn) (kn as unknown as { babylon?: unknown }).babylon = babylon;
  // P1-T06 test hook: jump the world clock (night shots, wet-road shots)
  if (kn)
    (kn as unknown as { setWorldTimeHours?: (h: number) => void }).setWorldTimeHours = (h: number) => {
      sky.clock = { worldTime: h * 3600 * 1000, timeScale: 1, serverNow: Date.now() };
    };
  // P1-T07 test hook: teleport the player (collider acceptance probes)
  if (kn)
    (kn as unknown as { teleport?: (x: number, y: number, z: number) => void }).teleport = (x, y, z) =>
      player.teleport(x, y, z);
  // P1-T09 test hook: force the weather (R-05 noon-clear colour check)
  if (kn)
    (kn as unknown as { setWeather?: (k: string | null) => void }).setWeather = (k) => {
      sky.weatherOverride = k as never;
    };
  // P1-T12 test hook: precipitation particle stats + eased wetness
  if (kn)
    (
      kn as unknown as {
        weatherStats?: () => { rain: number; snow: number; rainRate: number; wet: number; skyRain: number };
      }
    ).weatherStats = () => ({ ...weatherFx.stats(), wet: CityEnv.wet, skyRain: sky.rain });
  // P1-T13 test hook: freeze/unfreeze all time-driven systems
  if (kn)
    (kn as unknown as { setFreeze?: (v: boolean) => void }).setFreeze = (v: boolean) => {
      freezeAt = v ? performance.now() : null;
      weatherFx.setFrozen(v);
      fountains.setFrozen(v);
      if (streaming) streaming.clockFrozen = v;
    };
  // P1-T12 test hook: pin shader wetness (null restores auto ease)
  if (kn)
    (kn as unknown as { setWet?: (v: number | null) => void }).setWet = (v) => {
      CityEnv.wetOverride = v;
    };
  if (kn)
    (kn as unknown as { playerState?: () => { x: number; y: number; z: number } }).playerState = () => ({
      x: player.state.x,
      y: player.state.y,
      z: player.state.z,
    });

  if (kn && net) kn.setNetSource?.(net);

  // reserved shed-step multipliers (crowd/props land in P1+); kept readable for __KN
  const scales = { crowd: 1, radius: 1 };
  const quality = new QualityController(
    tier,
    {
      get scale() {
        return engine.getHardwareScalingLevel();
      },
      applyScale(s) {
        engine.setHardwareScalingLevel(s);
        renderScaleNow = 1 / s;
      },
      steps: [
        () => {
          if (!pipeline || !pipeline.msaaEnabled) return false;
          pipeline.setMsaa(false);
          return true;
        },
        () => {
          if (!pipeline || !pipeline.bloomEnabled) return false;
          pipeline.setBloom(false);
          return true;
        },
        () => {
          if (!shadows) return false;
          shadows.setHalfRate(true);
          return true;
        },
        () => {
          scales.crowd *= 0.7; // no crowd in P0 — the step still applies (logged)
          return true;
        },
        () => {
          scales.radius *= 0.8; // trees/props land in P1+; step reserved
          return true;
        },
        () => {
          if (!shadows) return false;
          shadows.shrinkDistance(0.7);
          return true;
        },
      ],
      onShed(step) {
        const name = SHED_STEP_NAMES[step] ?? `step${step}`;
        shedLog.push(name);
        console.log(`[quality] shed: ${name}`);
      },
    },
    window.devicePixelRatio || 1,
  );

  const frames = new FrameStats();

  // pointer drag look + wheel (desktop)
  let dragging = false;
  let lastX = 0;
  let lastY = 0;
  canvas.addEventListener('pointerdown', (e) => {
    dragging = true;
    lastX = e.clientX;
    lastY = e.clientY;
  });
  window.addEventListener('pointerup', () => {
    dragging = false;
  });
  window.addEventListener('pointermove', (e) => {
    if (!dragging) return;
    camera.onPointerDelta(e.clientX - lastX, e.clientY - lastY);
    lastX = e.clientX;
    lastY = e.clientY;
  });
  canvas.addEventListener(
    'wheel',
    (e) => {
      camera.onWheel(e.deltaY);
    },
    { passive: true },
  );

  // touch controls
  const isTouch = matchMedia('(pointer: coarse)').matches;
  ui.setTouchMode(isTouch);
  if (isTouch) {
    input.attachTouch(
      document.getElementById('joystick') as HTMLElement,
      document.getElementById('joystick-knob') as HTMLElement,
      document.getElementById('btn-run') as HTMLElement,
    );
  }

  ui.setLoading('loading.ready', 1);
  const perfRequested = new URLSearchParams(location.search).has('perf');
  if (!perfRequested) setTimeout(() => ui.showIntro(), 200);
  if (kn) {
    maybeStartPerfRunner(scene, camera, kn, shedLog, () => {
      document.getElementById('intro')?.classList.add('hidden');
      document.getElementById('loading')?.classList.add('hidden');
    });
  }

  const district = 'MIDTOWN';
  let lastChipClock = '';
  let lastWeatherChip = '';

  let lastT = performance.now();
  // P1-T13 test hook: freeze the simulation clock (dt=0, every time source
  // pinned) so art-test can shoot a still pair for the z-fighting check
  let freezeAt: number | null = null;
  engine.runRenderLoop(() => {
    const rawNow = performance.now();
    const now = freezeAt ?? rawNow;
    const dtMs = freezeAt ? 0 : Math.min(100, rawNow - lastT);
    lastT = rawNow;

    const mi = input.read(camera.cam.alpha);
    player.setInput(mi.fwd, mi.turn, mi.strafeX, mi.strafeZ, mi.run);
    player.update(dtMs, now);
    camera.follow(player.state, player.state.yaw);
    camera.update(dtMs);
    remotes.update(now);
    if (blobs) {
      blobs.set('hero', player.state.x, player.state.z, true);
      remotes.forEachPosition((id, x, z) => blobs.set(id, x, z, true));
    } else if (shadows && remotesDirty) {
      // P1-T10: hero avatar and remote capsules cast (SPEC-01 §6)
      shadows.addCaster(player.root);
      for (const mesh of remotes.casterMeshes()) {
        if (!castRemotes.has(mesh.name)) {
          castRemotes.add(mesh.name);
          shadows.addCaster(mesh);
        }
      }
      remotesDirty = false;
    }

    sky.update(dtMs / 1000, Date.now());
    pipeline?.setNight(sky.night);
    streaming?.tick(dtMs / 1000);
    props.update(camera.cam.globalPosition.x, camera.cam.globalPosition.z, now);
    // P1-T11: crowd + traffic (real-time sim; the 30x world clock only gates
    // who is out and about)
    crowd?.update(
      dtMs,
      camera.cam.globalPosition.x,
      camera.cam.globalPosition.z,
      sky.hourOfDay,
      sky.dayNumber,
      sky.rain,
    );
    if (traffic) {
      traffic.update(dtMs, camera.cam.globalPosition.x, camera.cam.globalPosition.z);
      traffic.setNight(sky.night);
    }

    // P1-T08: water + fountain FX follow the same sources as the sky
    {
      const sd = sky.sunVec;
      const up = sd.y > 0;
      const k = up ? sky.sun.intensity * 0.6 : 0.25;
      water.update(
        camera.cam.globalPosition,
        {
          sunDir: up ? { x: sd.x, y: sd.y, z: sd.z } : { x: -sd.x, y: Math.abs(sd.y) + 0.25, z: -sd.z },
          sunColor: up
            ? {
                x: sky.sun.diffuse.r * k,
                y: sky.sun.diffuse.g * k,
                z: sky.sun.diffuse.b * k,
              }
            : { x: 0.2, y: 0.22, z: 0.3 },
          night: sky.night,
          overcast: sky.overcast,
          rain: sky.rain,
        },
        now / 1000,
      );
      fountains.update(camera.cam.globalPosition, now);
      fountains.setNight(sky.night);
    }

    // P1-T12: camera-following precipitation (emitter 4 m up, 12 m ahead)
    {
      const wp = sky.weatherParams;
      const fwd = camera.cam.getForwardRay().direction;
      weatherFx.update(camera.cam.globalPosition, fwd, wp, dtMs / 1000);
    }

    // P1-T06: feed city material plugins (wetness/snow easing, wind, night)
    envTimeS += dtMs / 1000;
    {
      const wp = sky.weatherParams;
      CityEnv.update(
        {
          rain: wp.rain,
          snow: wp.snow,
          wind: 0.45 + wp.wind * 1.4 + wp.storm * 1.2,
          night: sky.night,
          time: envTimeS,
        },
        dtMs / 1000,
      );
      cityMats.setNight(sky.night);
    }

    // HUD chips (cheap string compare first)
    const time = sky.timeStr(Date.now());
    const weatherLabel = weatherLabelFor(sky.weatherKind);
    if (time !== lastChipClock || weatherLabel !== lastWeatherChip) {
      lastChipClock = time;
      lastWeatherChip = weatherLabel;
      ui.setChips(district, time, weatherLabel);
    }

    // stats + adaptive quality (frozen by __KN.noAdapt)
    frames.push(now, engine.getDeltaTime());
    if (kn) {
      updateKnStats(kn, frames, engine, tier, tierInfo.gpu, renderScaleNow, shedLog);
      if (kn.__forceShed) {
        kn.__forceShed = false;
        quality.shed();
      }
      if (!kn.noAdapt) {
        quality.tick(now, { rafP95: frames.rafP95, workP95: frames.workP95, refresh: frames.refreshMs });
      }
    }
    ui.updateOverlay();

    scene.render();
  });

  window.addEventListener('resize', () => engine.resize());
}

function weatherLabelFor(kind: string): string {
  const labels: Record<string, string> = {
    clear: 'Clear',
    cloudy: 'Cloudy',
    rain: 'Rain',
    storm: 'Thunderstorm',
    fog: 'Fog',
    heatwave: 'Heatwave',
    snow: 'Snow',
  };
  return labels[kind] ?? kind;
}

function deviceInfoText(info: TierInfo, renderScale: number): string {
  return [
    `tier: ${info.tier} (source: ${info.source})`,
    `gpu: ${info.gpu}`,
    `render scale: ${renderScale.toFixed(2)}`,
    `dpr: ${(window.devicePixelRatio ?? 1).toFixed(2)}`,
    `shadows: on`,
    `physics: worker`,
  ].join('\n');
}

let lastStats = '';
/** set once installKN() runs (after initUi); powers the perf-HUD tap-to-shed */
let knRef: KNGlobal | null = null;
function statsLine(): string {
  return lastStats;
}

function updateKnStats(
  kn: KNGlobal,
  frames: FrameStats,
  engine: import('babylonjs').AbstractEngine,
  tier: Tier,
  gpu: string,
  renderScale: number,
  shed: string[],
): void {
  const s = (kn as unknown as { __lastStats?: Record<string, unknown> }).__lastStats;
  if (!s) return;
  const now = performance.now();
  if (now - (lastLineAt ?? 0) > 250) {
    lastLineAt = now;
    lastStats = [
      `fps ${String(s['fps'] ?? 0)} · p95 ${frames.rafP95.toFixed(1)} ms`,
      `draw ${String(s['drawCalls'] ?? 0)} · tris ${String(s['triangles'] ?? 0)}`,
      `js ${String(s['jsMs'] ?? 0)} ms · scale ${(1 / engine.getHardwareScalingLevel()).toFixed(2)}`,
      `tier ${tier} · shed ${shed.length}`,
      `patch p95 ${String(s['serverPatchLagP95Ms'] ?? 0)} ms`,
      `gpu ${gpu.slice(0, 28)}`,
    ].join('\n');
  }
  void renderScale;
  Object.assign(s, {
    fps: frames.fps,
    frameMsP50: frames.p50,
    frameMsP95: frames.workP95,
    frameMsMax: frames.max,
    renderScale,
    shed,
    shedSteps: shed,
  });
}
let lastLineAt: number | undefined;

boot().catch((err) => {
  console.error('[boot] failed', err);
  const step = document.getElementById('loading-step');
  if (step) step.textContent = `Boot failed: ${String((err as Error)?.message ?? err)}`;
});
